/**
 * Desenho no mapa via Terra Draw (substituto do Drawing Library depreciado).
 * @see https://developers.google.com/maps/documentation/javascript/examples/map-drawing-terradraw
 */
import { Injectable, NgZone } from "@angular/core";
import {
  TerraDraw,
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawRenderMode,
  GeoJSONStoreFeatures,
  TerraDrawGoogleMapsAdapter,
  TerraDrawInstance,
} from "../vendor/terra-draw";

type DrawFinishHandler = (feature: GeoJSONStoreFeatures) => void;

@Injectable({ providedIn: "root" })
export class MapTerraDrawService {
  private draw: TerraDrawInstance | null = null;
  private map: google.maps.Map | null = null;
  private readyPromise: Promise<TerraDrawInstance> | null = null;
  private finishHandler: DrawFinishHandler | null = null;
  private selectionFeatureId: string | null = null;

  constructor(private zone: NgZone) {}

  bindMap(map: google.maps.Map): void {
    if (this.map === map && this.draw) {
      return;
    }
    this.stop();
    this.map = map;
    this.readyPromise = null;
  }

  stop(): void {
    this.detachFinishListener();
    if (this.draw) {
      try {
        this.draw.stop();
      } catch {
        /* noop */
      }
      this.draw = null;
    }
    this.selectionFeatureId = null;
  }

  clearSelection(): void {
    if (this.draw && this.selectionFeatureId) {
      this.draw.removeFeatures([this.selectionFeatureId]);
      this.selectionFeatureId = null;
    }
  }

  cancelDrawing(): void {
    this.detachFinishListener();
    if (this.draw) {
      this.draw.setMode("static");
    }
  }

  featureToBounds(
    feature: GeoJSONStoreFeatures
  ): google.maps.LatLngBounds {
    const ring = this.getOuterRing(feature);
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;

    ring.forEach(([lng, lat]) => {
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    });

    return new google.maps.LatLngBounds(
      new google.maps.LatLng(minLat, minLng),
      new google.maps.LatLng(maxLat, maxLng)
    );
  }

  ringToLatLngs(feature: GeoJSONStoreFeatures): google.maps.LatLng[] {
    return this.getOuterRing(feature).map(
      ([lng, lat]) => new google.maps.LatLng(lat, lng)
    );
  }

  boundsToFencePositions(bounds: google.maps.LatLngBounds): string {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    return `${sw.lat()}!${sw.lng()}|${ne.lat()}!${ne.lng()}`;
  }

  latLngsToRoutePositions(points: google.maps.LatLng[]): string {
    return points
      .map((p, index) => {
        const suffix = index < points.length - 1 ? "|" : "";
        return `${p.lat()}!${p.lng()}${suffix}`;
      })
      .join("");
  }

  startRectangle(
    onComplete: (bounds: google.maps.LatLngBounds) => void,
    options?: { keepOnMap?: boolean }
  ): void {
    void this.runDrawMode("rectangle", (feature) => {
      const bounds = this.featureToBounds(feature);
      const featureId = feature.id as string;

      if (options?.keepOnMap) {
        this.selectionFeatureId = featureId;
      } else if (this.draw && featureId) {
        this.draw.removeFeatures([featureId]);
      }

      this.zone.run(() => onComplete(bounds));
    });
  }

  startPolyline(onComplete: (points: google.maps.LatLng[]) => void): void {
    void this.runDrawMode("linestring", (feature) => {
      const points = this.ringToLatLngs(feature);
      const featureId = feature.id as string;
      if (this.draw && featureId) {
        this.draw.removeFeatures([featureId]);
      }
      this.zone.run(() => onComplete(points));
    });
  }

  startPolygon(onComplete: (points: google.maps.LatLng[]) => void): void {
    void this.runDrawMode("polygon", (feature) => {
      const points = this.ringToLatLngs(feature);
      const featureId = feature.id as string;
      if (this.draw && featureId) {
        this.draw.removeFeatures([featureId]);
      }
      this.zone.run(() => onComplete(points));
    });
  }

  private async runDrawMode(
    mode: "rectangle" | "linestring" | "polygon",
    onComplete: DrawFinishHandler
  ): Promise<void> {
    const draw = await this.ensureReady();
    this.detachFinishListener();
    this.finishHandler = (feature) => {
      this.detachFinishListener();
      draw.setMode("static");
      onComplete(feature);
    };
    draw.on("finish", this.onFinish);
    draw.setMode(mode);
  }

  private onFinish = (id: string): void => {
    if (!this.draw || !this.finishHandler) {
      return;
    }
    const feature = this.draw.getSnapshotFeature(id);
    if (feature) {
      this.finishHandler(feature);
    }
  };

  private detachFinishListener(): void {
    if (this.draw && this.finishHandler) {
      this.draw.off("finish", this.onFinish);
    }
    this.finishHandler = null;
  }

  private getOuterRing(feature: GeoJSONStoreFeatures): number[][] {
    const geometry = feature.geometry as {
      type: string;
      coordinates: number[][] | number[][][];
    };

    if (geometry.type === "Polygon") {
      return geometry.coordinates[0] as number[][];
    }

    if (geometry.type === "LineString") {
      return geometry.coordinates as number[][];
    }

    throw new Error(`Geometria não suportada: ${geometry.type}`);
  }

  private ensureReady(): Promise<TerraDrawInstance> {
    if (this.draw) {
      return Promise.resolve(this.draw);
    }
    if (!this.map) {
      return Promise.reject(new Error("Mapa não inicializado"));
    }
    if (this.readyPromise) {
      return this.readyPromise;
    }

    const map = this.map;
    this.readyPromise = new Promise((resolve, reject) => {
      const init = (): void => {
        try {
          const draw = new TerraDraw({
            adapter: new TerraDrawGoogleMapsAdapter({
              map,
              lib: google.maps,
              coordinatePrecision: 9,
            }),
            modes: [
              new TerraDrawRenderMode({
                modeName: "static",
                styles: {},
              }),
              new TerraDrawRectangleMode({
                styles: {
                  fillColor: "#1976d2",
                  fillOpacity: 0.15,
                  outlineColor: "#1976d2",
                  outlineWidth: 2,
                },
              }),
              new TerraDrawLineStringMode({
                styles: {
                  lineStringColor: "#d32f2f",
                  lineStringWidth: 3,
                },
              }),
              new TerraDrawPolygonMode({
                styles: {
                  fillColor: "#1976d2",
                  fillOpacity: 0.15,
                  outlineColor: "#1976d2",
                  outlineWidth: 2,
                },
              }),
            ],
          });

          draw.on("ready", () => {
            this.draw = draw;
            draw.start();
            draw.setMode("static");
            resolve(draw);
          });

          draw.start();
        } catch (error) {
          reject(error);
        }
      };

      if (map.getProjection()) {
        init();
      } else {
        google.maps.event.addListenerOnce(map, "projection_changed", init);
      }
    });

    return this.readyPromise;
  }
}
